import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { productMessaging } from '../../config/messaging';

export const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/simulations', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const { error } = await signUp(email, password, fullName);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
      // Auto-redirect after signup success
      setTimeout(() => {
        navigate('/simulations');
      }, 2000);
    }
  };

  const handleGoogleSignUp = async () => {
    setError(null);
    setGoogleLoading(true);
    const { error } = await signInWithGoogle('/simulations');
    if (error) {
      setError(error.message);
      setGoogleLoading(false);
    }
  };

  if (success) {
    return (
      <div className="marketing-page">
        <MarketingHeader />
        <section className="marketing-hero">
          <div className="marketing-container">
            <div className="marketing-signup-success">
              <h1 className="marketing-hero-title">Welcome!</h1>
              <p className="marketing-hero-description">Your account has been created successfully.</p>
              <p className="marketing-hero-note">Redirecting you to the app...</p>
            </div>
          </div>
        </section>
        <MarketingFooter />
      </div>
    );
  }

  return (
    <div className="marketing-page">
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="marketing-container">
          <div className="marketing-signup-content">
            <div className="marketing-signup-header">
              <h1 className="marketing-hero-title">{productMessaging.auth.signupTitle}</h1>
              <p className="marketing-hero-subtitle">{productMessaging.auth.signupSubtitle}</p>
              <p className="marketing-hero-description">{productMessaging.auth.signupDescription}</p>
            </div>

            <div className="marketing-signup-form-container">
              <div className="marketing-oauth-actions">
                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={googleLoading}
                  className="marketing-btn marketing-btn-google marketing-btn-large"
                >
                  <span className="marketing-btn-google-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" focusable="false">
                      <path d="M17.64 9.2045c0-.638-.0573-1.252-.1636-1.842H9v3.481h4.84c-.2085 1.125-.8425 2.08-1.7915 2.717v2.258h2.8975c1.6965-1.5625 2.694-3.864 2.694-6.6145z" fill="#4285F4"/>
                      <path d="M9 18c2.43 0 4.4675-.806 5.9565-2.181l-2.8975-2.258c-.805.54-1.837.859-3.059.859-2.352 0-4.3455-1.588-5.0565-3.72H.957977v2.332C2.43798 15.985 5.4805 18 9 18z" fill="#34A853"/>
                      <path d="M3.9435 10.7c-.1815-.54-.2845-1.118-.2845-1.7s.103-1.16.2845-1.7V4.968H.957977A8.996 8.996 0 000 9c0 1.456.3495 2.834.957977 4.032L3.9435 10.7z" fill="#FBBC05"/>
                      <path d="M9 3.579c1.32 0 2.506.453 3.4395 1.341l2.58-2.58C13.464 1.076 11.427 0 9 0 5.4805 0 2.43798 2.015 0.957977 4.968l2.985523 2.332C4.6545 5.955 6.648 3.579 9 3.579z" fill="#EA4335"/>
                    </svg>
                  </span>
                  <span>{googleLoading ? 'Redirecting…' : 'Continue with Google'}</span>
                </button>
              </div>

              <div className="marketing-auth-divider">
                <span>or</span>
              </div>

              <form onSubmit={handleSubmit} className="marketing-signup-form">
                <div className="marketing-form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    autoComplete="name"
                    className="marketing-form-input"
                  />
                </div>

                <div className="marketing-form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    className="marketing-form-input"
                  />
                </div>

                <div className="marketing-form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    minLength={6}
                    className="marketing-form-input"
                  />
                  <small className="marketing-form-hint">At least 6 characters</small>
                </div>

                {error && <div className="marketing-form-error">{error}</div>}

                <button type="submit" disabled={loading || googleLoading} className="marketing-btn marketing-btn-primary marketing-btn-large">
                  {loading ? 'Creating account...' : 'Start modeling'}
                </button>
              </form>

              <p className="marketing-signup-footer">
                Already have an account? <Link to="/login" className="marketing-nav-link">Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};
